import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import { Spinner, Badge, Button, Card, Row, Col } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { FiRefreshCw, FiSearch, FiUpload, FiCreditCard, FiDownload, FiX, FiUsers, FiClock, FiCheckCircle, FiAlertCircle, FiCreditCard as FiCard, FiCalendar, FiTrendingUp } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import ReportViewDownload from '../Shared/ViewDownloadReports';
import RecentReports from '../Shared/RecentReports';
import UploadModal from '../Shared/UploadModal';

const Reports = () => {
    const [reportsData, setReportsData] = useState([]);
    const [productCache, setProductCache] = useState({});
    const [bankAccountCache, setBankAccountCache] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDownloadMode, setIsDownloadMode] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState({});
    const [cancelTimer, setCancelTimer] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);

    const fetchingRef = useRef(false);
    const productFetchingRef = useRef({});
    const bankAccountFetchingRef = useRef({});

    const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");

    const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    const checkAndUpdateOverduePayments = useCallback(async () => {
        try {
            const today = moment().tz("Asia/Singapore");
            const currentMonthYear = today.format("MMM YYYY");
            let updatedCount = 0;

            for (const customer of reportsData) {
                if (!customer.reports || !customer.payment_date) continue;

                const customerDueDate = moment(customer.payment_date).tz("Asia/Singapore");
                const customerDueDay = customerDueDate.date();

                const dueDateThisMonth = moment().tz("Asia/Singapore")
                    .date(customerDueDay)
                    .hour(0).minute(0).second(0).millisecond(0);

                if (today.isSameOrAfter(dueDateThisMonth, 'day')) {
                    const currentMonthReport = customer.reports.find(report =>
                        report.month === currentMonthYear
                    );

                    if (currentMonthReport && currentMonthReport.status === "Pending") {
                        try {
                            await axios.put(
                                `https://www.azzenterprises.com/api/admin/${customer.customer_id}/report-status`,
                                {
                                    month: currentMonthYear,
                                    status: "Unpaid",
                                    comments: `Automatically updated - payment overdue on ${customerDueDay}${getOrdinalSuffix(customerDueDay)}`
                                },
                                { withCredentials: true }
                            );

                            setReportsData(prev => prev.map(cust => {
                                if (cust.customer_id === customer.customer_id) {
                                    const updatedReports = cust.reports.map(report =>
                                        report.month === currentMonthYear
                                            ? {
                                                ...report,
                                                status: "Unpaid",
                                                comments: `Automatically updated - payment overdue on ${customerDueDay}${getOrdinalSuffix(customerDueDay)}`,
                                                updated_time: new Date().toISOString()
                                            }
                                            : report
                                    );
                                    return { ...cust, reports: updatedReports };
                                }
                                return cust;
                            }));

                            updatedCount++;
                        } catch (error) {
                        }
                    }
                }
            }

            if (updatedCount > 0) {
                toast.success(`Updated ${updatedCount} overdue payments`);
            }
        } catch (error) {
        }
    }, [reportsData]);

    useEffect(() => {
        if (reportsData.length > 0 && !loading) {
            checkAndUpdateOverduePayments();
        }
    }, [reportsData, loading, checkAndUpdateOverduePayments]);

    const fetchReports = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/customers/getreports");
            setReportsData(res.data.allReports || []);
        } catch (err) {
            toast.error("Failed to fetch reports");
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const fetchBankAccountNumber = useCallback(async (boDDARefNo) => {
        if (!boDDARefNo || bankAccountCache[boDDARefNo] || bankAccountFetchingRef.current[boDDARefNo]) {
            return bankAccountCache[boDDARefNo] || null;
        }

        bankAccountFetchingRef.current[boDDARefNo] = true;

        try {
            setLoadingBankAccounts(prev => ({ ...prev, [boDDARefNo]: true }));

            const response = await axios.get(
                `https://www.azzenterprises.com/api/admin/bank-account/${boDDARefNo}`,
                { withCredentials: true }
            );

            const bankAccountData = response.data.applicantBankAccNo || "—";

            setBankAccountCache(prev => ({
                ...prev,
                [boDDARefNo]: bankAccountData
            }));

            return bankAccountData;
        } catch (error) {
            setBankAccountCache(prev => ({
                ...prev,
                [boDDARefNo]: "—"
            }));
            return "—";
        } finally {
            setLoadingBankAccounts(prev => ({ ...prev, [boDDARefNo]: false }));
            bankAccountFetchingRef.current[boDDARefNo] = false;
        }
    }, [bankAccountCache]);

    const fetchAllBankAccounts = useCallback(async () => {
        const rowsWithDDARef = reportsData.filter(row => row.boDDARefNo && !bankAccountCache[row.boDDARefNo] && !bankAccountFetchingRef.current[row.boDDARefNo]);

        if (rowsWithDDARef.length === 0) return;

        try {
            const promises = rowsWithDDARef.map(async (row) => {
                if (row.boDDARefNo) {
                    return fetchBankAccountNumber(row.boDDARefNo);
                }
            });

            await Promise.all(promises);
        } catch (error) {
        }
    }, [reportsData, bankAccountCache, fetchBankAccountNumber]);

    useEffect(() => {
        if (reportsData.length > 0) {
            fetchAllBankAccounts();
        }
    }, [reportsData, fetchAllBankAccounts]);

    const fetchProductData = useCallback(async (productId) => {
        if (!productId || productFetchingRef.current[productId]) {
            return productCache[productId] || null;
        }

        productFetchingRef.current[productId] = true;

        try {
            const res = await axios.get(
                `https://www.azzenterprises.com/api/products/info/${productId}`
            );
            const productData = {
                product_name: res.data.product_name || "—",
                color: res.data.color || "—",
                capacity: res.data.capacity || "—",
                size_and_weight: res.data.size_and_weight || "—",
                display: res.data.display || "—",
                product_description: res.data.product_description || "—",
                price: res.data.price || 0,
                quantity: res.data.quantity || 0,
                status: res.data.status || "—",
                date: res.data.date || "—",
                product_img: res.data.product_img || "—",
                discount_price: res.data.discount_price || 0,
                fullData: true
            };

            setProductCache((prev) => ({ ...prev, [productId]: productData }));
            return productData;
        } catch {
            const errorData = { product_name: "—", fullData: true };
            setProductCache((prev) => ({ ...prev, [productId]: errorData }));
            return errorData;
        } finally {
            productFetchingRef.current[productId] = false;
        }
    }, [productCache]);

    const getProductName = (productId) => {
        if (!productId) return "—";
        const cachedData = productCache[productId];
        if (cachedData) return cachedData.product_name || "—";

        if (!productFetchingRef.current[productId]) {
            fetchProductData(productId);
        }

        return "Loading...";
    };

    const getBankAccountNumber = (boDDARefNo) => {
        if (!boDDARefNo) return "—";

        const cachedAccount = bankAccountCache[boDDARefNo];
        if (cachedAccount) return cachedAccount;

        if (boDDARefNo && boDDARefNo !== "—" && !bankAccountFetchingRef.current[boDDARefNo]) {
            fetchBankAccountNumber(boDDARefNo);
        }

        return loadingBankAccounts[boDDARefNo] ? "Loading..." : "—";
    };

    const renderProductTooltip = (productId) => {
        if (!productId) return <Tooltip>No product data</Tooltip>;

        const productData = productCache[productId];

        if (!productData || !productData.fullData) {
            return <Tooltip>Loading product details...</Tooltip>;
        }

        return (
            <Tooltip style={{ maxWidth: "400px" }}>
                <div className="text-start">
                    <strong className="d-block border-bottom pb-1 mb-1">{productData.product_name}</strong>
                    <div className="small">
                        <div><strong>Color:</strong> {productData.color}</div>
                        <div><strong>Capacity:</strong> {productData.capacity}</div>
                        <div><strong>Size & Weight:</strong> {productData.size_and_weight}</div>
                        <div><strong>Display:</strong> {productData.display}</div>
                        <div><strong>Price:</strong> ${productData.price}</div>
                        <div><strong>Quantity:</strong> {productData.quantity}</div>
                        <div><strong>Status:</strong> {productData.status}</div>
                        {productData.product_description && (
                            <div className="mt-1">
                                <strong>Description:</strong>
                                <div className="text-truncate" style={{ maxWidth: "350px" }}>
                                    {productData.product_description}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Tooltip>
        );
    };

    const getMonthReport = (reports, monthOffset = 0) => {
        const month = moment()
            .tz("Asia/Singapore")
            .add(monthOffset, "month")
            .format("MMM YYYY");
        const report = (reports || []).find((r) => r.month === month);
        return { month, status: report ? report.status : null };
    };

    const renderStatusBadge = (reports, monthOffset = 0) => {
        const { month, status } = getMonthReport(reports, monthOffset);
        if (!status) return <span>—</span>;

        const colorCode =
            status === "Paid"
                ? "#16a34a"
                : status === "Unpaid"
                    ? "#dc2626"
                    : status === "Pending"
                        ? "#d97706"
                        : "#6b7280";

        return (
            <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">{month.split(' ')[0]}</span>
                <span
                    style={{
                        backgroundColor: colorCode,
                        color: "#fff",
                        fontSize: "0.6rem",
                        fontWeight: "500",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        display: "inline-block"
                    }}
                >
                    {status}
                </span>
            </div>
        );
    };

    const isPaymentOverdue = (customer) => {
        if (!customer.payment_date) return false;

        const today = moment().tz("Asia/Singapore").startOf('day');
        const customerDueDate = moment(customer.payment_date).tz("Asia/Singapore");
        const customerDueDay = customerDueDate.date();

        const dueDateThisMonth = moment().tz("Asia/Singapore")
            .date(customerDueDay)
            .startOf('day');

        return today.isAfter(dueDateThisMonth, 'day');
    };

    const formatDueDate = (paymentDate, row) => {
        if (!paymentDate) return {
            day: "—",
            fullDate: "No date",
            isOverdue: false,
            ordinalDay: "—",
            displayDate: "—",
            isNextMonth: false
        };

        const baseDate = moment(paymentDate).tz("Asia/Singapore");
        const { currentMonthStatus } = calculateEMIDetails(row);
        let targetMonthDue;
        let isNextMonth = false;

        if (currentMonthStatus === "Paid") {
            targetMonthDue = moment().tz("Asia/Singapore")
                .add(1, 'month')
                .date(baseDate.date())
                .startOf('day');
            isNextMonth = true;
        } else {
            targetMonthDue = moment().tz("Asia/Singapore")
                .date(baseDate.date())
                .startOf('day');
            isNextMonth = false;
        }

        const today = moment().tz("Asia/Singapore").startOf('day');
        const isOverdue = today.isAfter(targetMonthDue, 'day');

        const day = targetMonthDue.date();
        const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
        const month = targetMonthDue.format("MMM");
        const displayDate = `${ordinalDay} ${month}`;

        return {
            day: targetMonthDue.format("DD"),
            fullDate: targetMonthDue.format("DD MMM YYYY"),
            isOverdue: isOverdue,
            ordinalDay: ordinalDay,
            displayDate: displayDate,
            targetMonth: targetMonthDue.format("MMM YYYY"),
            isNextMonth: isNextMonth
        };
    };

    const formatCurrency = (amount) => {
        if (!amount) return "—";
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount);
    };

    const formatBankAccountNumber = (accountNumber) => {
        if (!accountNumber || accountNumber === "—") return "—";
        if (accountNumber.length <= 4) return accountNumber;

        const firstFour = accountNumber.slice(0, 4);
        const lastFour = accountNumber.slice(-4);
        return `${firstFour}****${lastFour}`;
    };

    const calculateEMIDetails = (row) => {
        const billAmount = row.bill_amount || 0;
        const emiPlan = row.emi_plan_duration || 1;
        const reports = row.reports || [];

        const monthlyEMI = billAmount / emiPlan;
        const paidMonths = reports.filter(r => r.status === "Paid").length;
        const paidAmount = paidMonths * monthlyEMI;
        const pendingAmount = billAmount - paidAmount;
        const currentMonthReport = reports.find(r => r.month === currentMonth);

        return {
            monthlyEMI,
            paidAmount,
            pendingAmount,
            paidMonths,
            totalMonths: emiPlan,
            currentMonthStatus: currentMonthReport?.status
        };
    };

    const filteredReports = reportsData.filter(
        (r) =>
            r.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.product_id && r.product_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (r.boDDARefNo && r.boDDARefNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (r.applicantBankCode && r.applicantBankCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (bankAccountCache[r.boDDARefNo] && bankAccountCache[r.boDDARefNo].toLowerCase().includes(searchTerm.toLowerCase())) ||
            (productCache[r.product_id] &&
                productCache[r.product_id].product_name &&
                productCache[r.product_id].product_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const financialStats = useMemo(() => {
        let totalPendingAmount = 0;
        let totalPaidAmount = 0;
        let todayMonthlyEMI = 0;
        let totalBillAmount = 0;

        filteredReports.forEach(row => {
            const { monthlyEMI, paidAmount, pendingAmount, currentMonthStatus } = calculateEMIDetails(row);

            const billAmount = parseFloat(row.bill_amount) || 0;
            totalBillAmount += billAmount;
            totalPendingAmount += pendingAmount;
            totalPaidAmount += paidAmount;

            if (currentMonthStatus === "Pending" || currentMonthStatus === "Unpaid") {
                todayMonthlyEMI += monthlyEMI;
            }
        });

        return {
            totalPendingAmount,
            totalPaidAmount,
            todayMonthlyEMI,
            totalBillAmount
        };
    }, [filteredReports]);

    const statusCounts = useMemo(() => {
        return {
            Pending: filteredReports.filter((r) =>
                r.reports?.some((rep) => rep.month === currentMonth && rep.status === "Pending")
            ).length,
            Paid: filteredReports.filter((r) =>
                r.reports?.some((rep) => rep.month === currentMonth && rep.status === "Paid")
            ).length,
            Unpaid: filteredReports.filter((r) =>
                r.reports?.some((rep) => rep.month === currentMonth && rep.status === "Unpaid")
            ).length,
        };
    }, [filteredReports, currentMonth]);

    const totalSelectedDeductionAmount = useMemo(() => {
        return selectedRows.reduce((total, row) => {
            const { monthlyEMI, currentMonthStatus } = calculateEMIDetails(row);
            if (currentMonthStatus === "Pending" || currentMonthStatus === "Unpaid") {
                return total + monthlyEMI;
            }
            return total;
        }, 0);
    }, [selectedRows]);

    const selectedStatusCounts = useMemo(() => {
        return {
            Paid: selectedRows.filter(row => {
                const { currentMonthStatus } = calculateEMIDetails(row);
                return currentMonthStatus === "Paid";
            }).length,
            Pending: selectedRows.filter(row => {
                const { currentMonthStatus } = calculateEMIDetails(row);
                return currentMonthStatus === "Pending";
            }).length,
            Unpaid: selectedRows.filter(row => {
                const { currentMonthStatus } = calculateEMIDetails(row);
                return currentMonthStatus === "Unpaid";
            }).length
        };
    }, [selectedRows]);

    const getRecentUpdates = useMemo(() => {
        const today = moment().tz("Asia/Singapore").startOf('day');
        const recentUpdates = [];
        const allUpdates = [];

        reportsData.forEach(customer => {
            if (customer.reports && Array.isArray(customer.reports)) {
                customer.reports.forEach(report => {
                    if (report.updated_time && report.updated_time !== null) {
                        const isSingaporeFormat = /^\d{1,2} \w{3} \d{4}, \d{1,2}:\d{2} [AP]M$/.test(report.updated_time);

                        if (isSingaporeFormat) {
                            try {
                                const updatedTime = moment(report.updated_time, 'DD MMM YYYY, hh:mm A').tz("Asia/Singapore");
                                const isToday = updatedTime.isSame(today, 'day');

                                const updateData = {
                                    customerName: customer.full_name,
                                    month: report.month,
                                    status: report.status,
                                    updated_time: report.updated_time,
                                    comments: report.comments,
                                    timestamp: updatedTime,
                                    isToday: isToday
                                };

                                allUpdates.push(updateData);

                                if (isToday) {
                                    recentUpdates.push(updateData);
                                }
                            } catch (error) {
                                console.error('Error parsing Singapore format date:', report.updated_time);
                            }
                        }
                    }
                });
            }
        });

        if (recentUpdates.length > 0) {
            return recentUpdates.sort((a, b) => b.timestamp - a.timestamp);
        }
        return allUpdates.sort((a, b) => b.timestamp - a.timestamp);
    }, [reportsData]);

    const RecentUpdatesBadge = () => {
        if (getRecentUpdates.length === 0) return null;

        return (
            <div className="my-3 p-3 rounded bg-light border">
                <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center flex-wrap gap-1">
                        <FiCheckCircle size={14} className="text-success me-1" />
                        <span className="small fw-semibold text-nowrap">Today's updates ({getRecentUpdates.length})</span>
                        {getRecentUpdates.map((update, index) => (
                            <span key={index} className="text-nowrap text-muted" style={{ fontSize: "13px", fontWeight: 500 }}>
                                {update.customerName}
                                <span className={
                                    update.status === 'Paid' ? 'text-success' :
                                        update.status === 'Unpaid' ? 'text-danger' : 'text-warning'
                                }>
                                    ({update.status} - {update.comments})
                                </span>
                                {` ${update.updated_time.split(', ')[1]}`}
                                {index < getRecentUpdates.length - 1 && <span className="text-muted">, </span>}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            width: "60px",
            sortable: true
        },
        {
            name: "CUSTOMER",
            selector: (row) => row.full_name,
            sortable: true,
            width: "160px",
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: "150px" }} title={row.full_name}>
                    {row.full_name}
                </div>
            )
        },
        {
            name: "PRODUCT",
            cell: (row) => (
                <OverlayTrigger
                    placement="right"
                    overlay={renderProductTooltip(row.product_id)}
                    delay={{ show: 250, hide: 0 }}
                >
                    <span
                        style={{
                            cursor: "pointer",
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "120px"
                        }}
                        className="text-gray-700"
                    >
                        {getProductName(row.product_id)}
                    </span>
                </OverlayTrigger>
            ),
            sortable: false,
            width: "140px",
        },
        {
            name: "DDA REF",
            cell: (row) => (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>DDA Reference Number</Tooltip>}
                >
                    <span className="font-semibold text-gray-600 small">
                        {row.boDDARefNo || "—"}
                    </span>
                </OverlayTrigger>
            ),
            width: "120px",
            sortable: false,
        },
        {
            name: "BANK ACCOUNT",
            cell: (row) => {
                const bankAccount = getBankAccountNumber(row.boDDARefNo);
                const isLoading = loadingBankAccounts[row.boDDARefNo];

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                {isLoading ? "Loading..." : bankAccount === "—" ? "No account found" : `Account: ${bankAccount}`}
                            </Tooltip>
                        }
                    >
                        <div className="d-flex align-items-center gap-1">
                            {isLoading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <FiCreditCard className="text-gray-500" size={12} />
                            )}
                            <span className="font-semibold text-gray-700 small">
                                {isLoading ? "Loading..." : formatBankAccountNumber(bankAccount)}
                            </span>
                        </div>
                    </OverlayTrigger>
                );
            },
            width: "140px",
            sortable: false,
        },
        {
            name: "BILL AMOUNT",
            selector: (row) => parseFloat(row.bill_amount) || 0,
            cell: (row) => (
                <span className="font-semibold text-gray-800">
                    {formatCurrency(row.bill_amount)}
                </span>
            ),
            width: "110px",
            sortable: true,
        },
        {
            name: "MONTHLY EMI",
            selector: (row) => {
                const { monthlyEMI } = calculateEMIDetails(row);
                return monthlyEMI;
            },
            cell: (row) => {
                const { monthlyEMI, currentMonthStatus } = calculateEMIDetails(row);

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                Monthly EMI: {formatCurrency(monthlyEMI)}
                                <br />
                                Status: {currentMonthStatus || "Not started"}
                            </Tooltip>
                        }
                    >
                        <span style={{ fontWeight: 600 }} className={
                            currentMonthStatus === "Paid" ? "text-success font-semibold" :
                                currentMonthStatus === "Unpaid" ? "text-danger font-semibold" :
                                    "text-warning font-semibold"
                        }>
                            {formatCurrency(monthlyEMI)}
                        </span>
                    </OverlayTrigger>
                );
            },
            width: "120px",
            sortable: true,
        },
        {
            name: "PAID AMOUNT",
            selector: (row) => {
                const { paidAmount } = calculateEMIDetails(row);
                return paidAmount;
            },
            cell: (row) => {
                const { paidAmount } = calculateEMIDetails(row);
                return (
                    <span className="text-green-600 font-semibold">
                        {formatCurrency(paidAmount)}
                    </span>
                );
            },
            width: "120px",
            sortable: true,
        },
        {
            name: "PENDING AMOUNT",
            selector: (row) => {
                const { pendingAmount } = calculateEMIDetails(row);
                return pendingAmount;
            },
            cell: (row) => {
                const { pendingAmount } = calculateEMIDetails(row);
                return (
                    <span className="text-red-600 font-semibold">
                        {formatCurrency(pendingAmount)}
                    </span>
                );
            },
            width: "150px",
            sortable: true,
        },
        {
            name: "DUE DATE",
            selector: (row) => {
                const { day } = formatDueDate(row.payment_date, row);
                return parseInt(day) || 0;
            },
            sortable: true,
            cell: (row) => {
                const { displayDate, fullDate, isOverdue, isNextMonth } = formatDueDate(row.payment_date, row);
                const { currentMonthStatus } = calculateEMIDetails(row);

                const showOverdue = isOverdue && currentMonthStatus !== "Paid";

                const getDueDateColor = () => {
                    if (isNextMonth) return "#3b82f6";
                    if (showOverdue) return "#dc2626";
                    if (currentMonthStatus === "Paid") return "#16a34a";
                    if (currentMonthStatus === "Unpaid") return "#dc2626";
                    if (currentMonthStatus === "Pending") return "#d97706";
                    return "#6b7280";
                };

                const dueDateColor = getDueDateColor();
                const textColor = "#fff";

                const getTooltipText = () => {
                    if (isNextMonth) {
                        return `Next EMI due on ${fullDate} (Current month already paid)`;
                    }
                    return showOverdue
                        ? `OVERDUE! Payment was due on ${fullDate}`
                        : `EMI will be auto-deducted on ${fullDate}`;
                };

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{getTooltipText()}</Tooltip>}
                    >
                        <span
                            style={{
                                backgroundColor: dueDateColor,
                                color: textColor,
                                fontSize: "0.7rem",
                                fontWeight: "500",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                display: "inline-block",
                                lineHeight: "1",
                                cursor: "pointer"
                            }}
                        >
                            {displayDate}
                        </span>
                    </OverlayTrigger>
                );
            },
            width: "110px",
        },
        {
            name: "PREV MONTH",
            cell: (row) => renderStatusBadge(row.reports, -1),
            width: "130px"
        },
        {
            name: "CURR MONTH",
            cell: (row) => renderStatusBadge(row.reports, 0),
            width: "130px"
        },
        {
            name: "PROGRESS",
            width: "90px",
            cell: (row) => {
                const total = row.reports?.length || 0;
                const paid = row.reports?.filter((r) => r.status === "Paid").length || 0;
                const percentage = total > 0 ? (paid / total) * 100 : 0;

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{paid} of {total} months paid ({percentage.toFixed(0)}%)</Tooltip>}
                    >
                        <div>
                            <div className="small font-semibold text-center">{paid}/{total}</div>
                            <div className="progress" style={{ height: "4px", width: "60px", backgroundColor: "#e5e7eb" }}>
                                <div
                                    className="progress-bar"
                                    style={{
                                        width: `${percentage}%`,
                                        backgroundColor: percentage === 100 ? "#16a34a" : "#3b82f6"
                                    }}
                                ></div>
                            </div>
                        </div>
                    </OverlayTrigger>
                );
            },
        },
    ];

    const selectableRowSelected = useCallback(
        (row) => isDownloadMode && selectedRows.some((s) => s.full_name === row.full_name),
        [isDownloadMode, selectedRows]
    );

    const isSameSelection = useCallback((current, candidate) => {
        if (current.length !== candidate.length) return false;
        return candidate.every((row) => current.some((s) => s.full_name === row.full_name));
    }, []);

    const toggleStatusSelection = (status) => {
        if (!isDownloadMode) {
            toast.warn("Activate download mode first!");
            return;
        }

        const newSelectedStatuses = selectedStatuses.includes(status)
            ? selectedStatuses.filter(s => s !== status)
            : [...selectedStatuses, status];

        setSelectedStatuses(newSelectedStatuses);

        if (newSelectedStatuses.length === 0) {
            setSelectedRows([]);
            return;
        }

        const matchingRows = filteredReports.filter((r) =>
            r.reports?.some((rep) =>
                rep.month === currentMonth &&
                newSelectedStatuses.includes(rep.status)
            )
        );

        setSelectedRows(matchingRows);
    };

    const getStatusColor = (status) => {
        return status === "Paid"
            ? "#16a34a"
            : status === "Unpaid"
                ? "#dc2626"
                : status === "Pending"
                    ? "#d97706"
                    : "#6b7280";
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "Paid": return <FiCheckCircle size={14} />;
            case "Pending": return <FiClock size={14} />;
            case "Unpaid": return <FiAlertCircle size={14} />;
            default: return <FiUsers size={14} />;
        }
    };

    const handleSelectedRowsChange = useCallback(
        (state) => {
            const newSelected = state.selectedRows;
            if (isSameSelection(selectedRows, newSelected)) return;
            setSelectedRows(newSelected);

            const statusesInSelection = [...new Set(newSelected.map(row => {
                const { currentMonthStatus } = calculateEMIDetails(row);
                return currentMonthStatus;
            }))].filter(Boolean);

            setSelectedStatuses(statusesInSelection);
        },
        [selectedRows, isSameSelection]
    );

    const toggleDownloadMode = () => {
        const newMode = !isDownloadMode;
        setIsDownloadMode(newMode);
        if (!newMode) {
            setSelectedRows([]);
            setSelectedStatuses([]);
        } else {
            toast.info("Select customers to generate reports");
            const timer = setTimeout(() => {
                if (selectedRows.length === 0) {
                    setIsDownloadMode(false);
                    setSelectedRows([]);
                    setSelectedStatuses([]);
                    toast.info("Download mode cancelled due to inactivity");
                }
            }, 5000);

            setCancelTimer(timer);
        }
    };

    const handleClearOrCancel = () => {
        if (cancelTimer) {
            clearTimeout(cancelTimer);
            setCancelTimer(null);
        }
        if (selectedStatuses.length > 0 || selectedRows.length > 0) {
            setSelectedStatuses([]);
            setSelectedRows([]);
            toast.info("Selection cleared");
        } else {
            setIsDownloadMode(false);
            setSelectedRows([]);
            setSelectedStatuses([]);
        }
    };

    useEffect(() => {
        return () => {
            if (cancelTimer) {
                clearTimeout(cancelTimer);
            }
        };
    }, [cancelTimer]);

    useEffect(() => {
        if (isDownloadMode && selectedRows.length === 0 && !cancelTimer) {
            const timer = setTimeout(() => {
                setIsDownloadMode(false);
                setSelectedRows([]);
                setSelectedStatuses([]);
                toast.info("Download mode cancelled due to inactivity");
            }, 5000);

            setCancelTimer(timer);
        } else if (selectedRows.length > 0 && cancelTimer) {
            clearTimeout(cancelTimer);
            setCancelTimer(null);
        }
    }, [selectedRows.length, isDownloadMode, cancelTimer]);

    const StatusFilterButton = ({ status, count, isActive, onClick }) => (
        <Button
            size="sm"
            variant={isActive ? "dark" : "outline-dark"}
            className="d-flex align-items-center gap-2 border-0"
            style={{
                fontSize: "0.75rem",
                fontWeight: "500",
                backgroundColor: isActive ? getStatusColor(status) : "transparent",
                color: isActive ? "white" : getStatusColor(status),
                border: `1px solid ${getStatusColor(status)} !important`
            }}
            onClick={onClick}
        >
            {getStatusIcon(status)}
            {status}
            <Badge
                bg={isActive ? "light" : "dark"}
                text={isActive ? "dark" : "light"}
                style={{
                    fontSize: "0.65rem",
                    backgroundColor: isActive ? "white" : getStatusColor(status)
                }}
            >
                {count}
            </Badge>
        </Button>
    );

    const StatCard = ({ title, value, icon, color }) => (
        <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: "48px",
                            height: "48px",
                            backgroundColor: `${color}15`
                        }}
                    >
                        {React.cloneElement(icon, {
                            size: 20,
                            color: color
                        })}
                    </div>
                    <div>
                        <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>{title}</h6>
                        <h4 className="fw-bold mb-0" style={{ color: color, fontSize: "1.5rem" }}>{value}</h4>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );

    return (
        <div className="container-fluid py-2">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div>
                                <p className="fw-bold mb-0 d-flex align-items-center">
                                    Monitor customer EMI payments and generate reports
                                </p>
                            </div>
                        </div>
                        <div className="d-flex gap-2">

                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="d-flex align-items-center gap-2 border"
                                onClick={fetchReports}
                                disabled={loading}
                                style={{ fontSize: "12px" }}
                            >
                                <FiRefreshCw size={14} />
                                Refresh
                            </Button>

                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-flex align-items-center gap-2 border"
                                onClick={() => setShowUploadModal(true)}
                                style={{ fontSize: "12px" }}
                            >
                                <FiUpload size={14} />
                                Upload
                            </Button>
                            <Button
                                variant={isDownloadMode ? "dark" : "outline-dark"}
                                size="sm"
                                className="d-flex align-items-center gap-2 border-0"
                                onClick={toggleDownloadMode}
                                style={{
                                    fontSize: "12px",
                                    backgroundColor: isDownloadMode ? "#374151" : "transparent",
                                    color: isDownloadMode ? "white" : "#374151"
                                }}
                            >
                                <FiDownload size={14} />
                                {isDownloadMode ? "Download Mode" : "Download"}
                            </Button>
                        </div>
                    </div>
                </div>
                {!isDownloadMode && (
                    <><RecentUpdatesBadge /></>)}
            </div>

            {!isDownloadMode && (
                <>
                    <Row className="mb-4 g-3">
                        <Col md={3}>
                            <StatCard
                                title="Total Customers"
                                value={filteredReports.length}
                                icon={<FiUsers />}
                                color="#374151"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Pending"
                                value={statusCounts.Pending}
                                icon={<FiClock />}
                                color="#d97706"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Paid"
                                value={statusCounts.Paid}
                                icon={<FiCheckCircle />}
                                color="#16a34a"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Unpaid"
                                value={statusCounts.Unpaid}
                                icon={<FiAlertCircle />}
                                color="#dc2626"
                            />
                        </Col>
                    </Row>

                    <Row className="mb-4 g-3">
                        <Col md={3}>
                            <StatCard
                                title="Total Pending Amount"
                                value={formatCurrency(financialStats.totalPendingAmount)}
                                icon={<FiClock />}
                                color="#d97706"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Total Paid Amount"
                                value={formatCurrency(financialStats.totalPaidAmount)}
                                icon={<FiCheckCircle />}
                                color="#16a34a"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Today's Monthly EMI"
                                value={formatCurrency(financialStats.todayMonthlyEMI)}
                                icon={<FiCalendar />}
                                color="#3b82f6"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Total Bill Amount"
                                value={formatCurrency(financialStats.totalBillAmount)}
                                icon={<FiTrendingUp />}
                                color="#8b5cf6"
                            />
                        </Col>
                    </Row>
                </>
            )}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-gray-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-6">
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <FiSearch size={16} className="text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search customers, products, DDA references..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: "0.875rem" }}
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2 flex-wrap">
                                    {isDownloadMode && (
                                        <>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="d-flex align-items-center gap-2 border"
                                                onClick={handleClearOrCancel}
                                                style={{ fontSize: "0.875rem" }}
                                            >
                                                <FiX size={14} />
                                                {selectedRows.length > 0 ? "Clear" : "Cancel"}
                                            </Button>

                                            <div className="vr d-none d-md-block"></div>

                                            <div className="d-flex gap-2">
                                                {["Pending", "Paid", "Unpaid"].map((status) => (
                                                    <StatusFilterButton
                                                        key={status}
                                                        status={status}
                                                        count={statusCounts[status]}
                                                        isActive={selectedStatuses.includes(status)}
                                                        onClick={() => toggleStatusSelection(status)}
                                                    />
                                                ))}
                                            </div>

                                            {selectedRows.length > 0 && (
                                                <>
                                                    <div className="vr d-none d-md-block"></div>
                                                    <Badge
                                                        bg="dark"
                                                        className="d-flex align-items-center px-2 py-1"
                                                        style={{ fontSize: "0.75rem" }}
                                                    >
                                                        {selectedRows.length} selected
                                                    </Badge>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {isDownloadMode && selectedRows.length > 0 && (
                        <div className="p-3 border-bottom bg-gray-50">
                            <ReportViewDownload
                                selectedRows={selectedRows}
                                selectedStatusCounts={selectedStatusCounts}
                                totalSelectedDeductionAmount={totalSelectedDeductionAmount}
                                formatCurrency={formatCurrency}
                                getProductName={getProductName}
                                getBankAccountNumber={getBankAccountNumber}
                                onClearSelection={handleClearOrCancel}
                                productCache={productCache}
                                bankAccountCache={bankAccountCache}
                            />
                        </div>
                    )}

                    <div className="p-3">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <Spinner animation="border" variant="secondary" />
                                <span className="ms-2 text-muted">Loading reports...</span>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={filteredReports}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                paginationComponentOptions={{
                                    rowsPerPageText: 'Rows per page:',
                                    rangeSeparatorText: 'of',
                                    selectAllRowsItem: true,
                                    selectAllRowsItemText: 'All',
                                }}
                                selectableRows={isDownloadMode}
                                selectableRowsHighlight
                                onSelectedRowsChange={handleSelectedRowsChange}
                                clearSelectedRows={!isDownloadMode}
                                selectableRowSelected={selectableRowSelected}
                                highlightOnHover
                                responsive
                                striped
                                persistTableHead
                                fixedHeader
                                fixedHeaderScrollHeight="calc(100vh - 300px)"
                                customStyles={{
                                    headCells: {
                                        style: {
                                            backgroundColor: "#f9fafb",
                                            fontWeight: "600",
                                            fontSize: "0.75rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            color: "#374151",
                                            borderBottom: "1px solid #e5e7eb"
                                        },
                                    },
                                    cells: {
                                        style: {
                                            fontSize: "0.875rem",
                                            paddingTop: "12px",
                                            paddingBottom: "12px",
                                            color: "#374151"
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
                <Card.Body>
                    {/* <h5 className="fw-bold mb-3" style={{ fontSize: "1.125rem" }}>Recent Activity</h5> */}
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-3">
                            <Spinner animation="border" size="sm" variant="secondary" />
                            <span className="ms-2 text-muted">Loading recent activity...</span>
                        </div>
                    ) : (
                        <RecentReports />
                    )}
                </Card.Body>
            </Card>

            <ToastContainer
                position="top-right"
                autoClose={2000}
                newestOnTop
                theme="light"
            />
            <UploadModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onUploadSuccess={() => {
                    fetchReports();
                    setShowUploadModal(false);
                }}
            />
        </div>
    );
};

export default Reports;